import { getViesOrg, fetchLegalForms, getSubCityByZipCode, getOpenStreetMapAddress } from './dataAccess.js';

export const extractCompanyNumberFromVat = (vatNumber) => {
  if (!vatNumber) {
    return null;
  }
  if (vatNumber.startsWith('BE')) {
    const digits = vatNumber.replace(/^BE/, '').replace(/\D/g, '').padStart(10, '0');
    return `${digits.slice(0, 4)}.${digits.slice(4, 7)}.${digits.slice(7, 10)}`;
  }
  if (vatNumber.startsWith('FR')) {
    // Format: FR + 2 check digits + 9-digit SIREN (= unique company identifier with 9 digits)
    const allDigits = vatNumber.replace(/^FR/, '').replace(/\D/g, '');
    // Skip first 2 check digits, take next 9 digits for SIREN
    const siren = allDigits.slice(2, 11).padStart(9, '0');
    return `${siren.slice(0, 3)} ${siren.slice(3, 6)} ${siren.slice(6, 9)}`;
  }
  if (vatNumber.startsWith('NL')) {
    // NL vat number = NL + kvk-number (8 digits) + 1 check digit + 'B' 2 digits for vestiging
    const digits = vatNumber.replace(/^NL/, '').replace(/\D/g, '');
    // Take first 8 digits for KvK number
    const kvkNumber = digits.slice(0, 8);
    return kvkNumber;
  }
  if (vatNumber.startsWith('DE')) {
    // German VAT number format: DE + 9-digit USt-IdNr (Umsatzsteuer-Identifikationsnummer)
    // Example: DE123456789 (no check digits)
    // 
    // USt-IdNr is used for:
    // - VAT purposes within EU
    // - Business-to-business transactions
    // - Official tax correspondence
    // 
    // Note: This is different from:
    // - Handelsregisternummer (HRB/HRA) - used for company registration
    // - Steuernummer (format varies by Bundesland) - used for domestic tax purposes
    const digits = vatNumber.replace(/^DE/, '').replace(/\D/g, '').padStart(9, '0');
    return digits; // Return as plain 9-digit string: "123456789"
  }
  if (vatNumber.startsWith('PL')) {
    return vatNumber.replace(/^PL/, '').replace(/\D/g, '').padStart(10, '0');
  }
  if (vatNumber.startsWith('FI')) {
    const digits = vatNumber.replace(/^FI/, '').replace(/\D/g, '').padStart(9, '0');
    return `${digits.slice(0, 8)}-${digits.slice(8, 9)}`;
  }
  return null;
};

export const validateCompanyNumber = (companyNumber, countryCode) => {
  if (!companyNumber) {
    return [];
  }
  const errors = [];
  if (!countryCode || countryCode === 'be') {
    if (!/^\d{4}\.\d{3}\.\d{3}$/.test(companyNumber)) {
      errors.push({
        code: 'companynumber.invalid',
        message: countryCode
          ? 'Een Belgisch ondernemingsnummer moet het formaat 1234.567.890 hebben.'
          : 'Het ondernemingsnummer voldoet niet aan het Belgisch formaat. Als het om een onderneming uit het buitenland gaat, vul dan het adres in.',
      });
    }
  } else if (countryCode === 'nl') {
    if (!/^\d{8}$/.test(companyNumber)) {
      errors.push({
        code: 'companynumber.invalid',
        message: 'Een Nederlands ondernemingsnummer (KvK-nummer) moet 8 aaneensluitende cijfers bevatten.',
      });
    }
  } else if (countryCode === 'fr') {
    if (!/^\d{3}\s\d{3}\s\d{3}$/.test(companyNumber)) {
      errors.push({
        code: 'companynumber.invalid',
        message: 'Een Frans ondernemingsnummer (SIREN) moet 9 cijfers bevatten, gescheiden door spaties.',
      });
    }
  } else if (countryCode === 'de') {
    if (!/^\d{9}$/.test(companyNumber)) {
      errors.push({
        code: 'companynumber.invalid',
        message: 'Een Duits ondernemingsnummer (USt-IdNr) moet 9 aaneensluitende cijfers bevatten.',
      });
    }
  }
  return errors;
}

export const validateVatNumber = (vatNumber, countryCode, companyNumber) => {
  if (!vatNumber) {
    return [];
  }
  const errors = [];
  if (countryCode && vatNumber.slice(0, 2).toUpperCase() !== countryCode.toUpperCase()) {
    errors.push({
      code: 'vatnumber.invalid',
      message: `Het BTW-nummer komt niet overeen met de landcode (${countryCode.toUpperCase()}) van het adres.`,
    });
  }
  if (!countryCode || countryCode === 'be') {
    if (!/^BE\d{10}$/.test(vatNumber)) {
      errors.push({
        code: 'vatnumber.invalid',
        message: countryCode
          ? 'Een Belgish BTW-nummer moet beginnen met "BE" gevolgd door 10 cijfers (het ondernemingsnummer).'
          : 'Het BTW-nummer voldoet niet aan het Belgisch formaat. Als het om een onderneming uit het buitenland gaat, vul dan het adres in.',
      });
    }
  }
  if (countryCode === 'nl') {
    if (!/^NL\d{9}B\d{2}$/.test(vatNumber)) {
      errors.push({
        code: 'vatnumber.invalid',
        message: 'Een Nederlands BTW-nummer moet beginnen met "NL" gevolgd door het kvk-nummer van 8 cijfers, een contrlonummer, de letter B en 2 cijfers voor de vestiging.',
      });
    }
  }
  if (countryCode === 'fr') {
    if (!/^FR\d{11}$/.test(vatNumber)) {
      errors.push({
        code: 'vatnumber.invalid',
        message: 'Een Frans BTW-nummer moet beginnen met "FR" gevolgd door 2 controle cijfers en het SIREN (9 cijfers).',
      });
    }
  }
  if (countryCode === 'de') {
    if (!/^DE\d{9}$/.test(vatNumber)) {
      errors.push({
        code: 'vatnumber.invalid',
        message: 'Een Duits BTW-nummer moet beginnen met "DE" gevolgd door 9 cijfers (USt-IdNr).',
      });
    }
  }
  const extractedCompanyNumberFromVat = extractCompanyNumberFromVat(vatNumber);
  if (companyNumber && extractedCompanyNumberFromVat && companyNumber !== extractedCompanyNumberFromVat) {
    errors.push({
      code: 'vatnumber.mismatch',
      message: 'Het opgegeven ondernemingsnummer komt niet overeen met het BTW-nummer.', 
    });
  }
  return errors;
}

const toTitleCase = (str) => {
  if (str === str.toUpperCase()) {
    // Only convert the string if it is all capital letters
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  }
  return str;
};

const isLikelyEenmanszaak = (viesName) => {
  // Check if name follows "Lastname, Firstname" pattern
  // Support Unicode letters (including accented characters like ç, é, ü, etc.)
  const personNamePattern = /^([\p{Lu}][\p{Ll}]+(\s+[\p{Lu}][\p{Ll}]+)*),\s+[\p{Lu}][\p{Ll}]+(\s+[\p{Lu}][\p{Ll}]+)*$/u;
  return personNamePattern.test(viesName);
};

const splitLegalFormAndName = (fullName, legalFormList) => {
  const legalFormMap = Object.fromEntries(legalFormList.map((legalForm) => [legalForm.code.toLowerCase(), legalForm]));
  legalFormMap.sa = legalFormMap['nv']; // 'sa' is french for 'nv' (société anonyme)
  legalFormMap.sarl = legalFormMap['bv']; // 'sarl' is french for 'bv' (société à responsabilité limitée)
  legalFormMap.sparl = legalFormMap['bv']; // 'sparl' is french for 'bv' (société par actions à responsabilité limitée)
  legalFormMap.srl = legalFormMap['bv']; // 'sarl' is french for 'bv' (société à responsabilité limitée)
  legalFormMap.asbl = legalFormMap['vzw']; // 'asbl' is french for 'vzw' (association sans but lucratif)
  legalFormMap.aisbl = legalFormMap['vzw']; // 'aisbl' is french for 'vzw' (association internationale sans but lucratif)
  legalFormMap['forme fs'] = legalFormMap['publiek recht']; // 'forme fs' is french for Andere vorm met een sociaal oogmerk van publiek recht (forme fs)
  legalFormMap.communes = legalFormMap['gemeente']; // 'communes' is french for 'gemeente' (local government)
  legalFormMap['b.v.'] = legalFormMap['bv']; // sometimes dots are used 'b.v.'
  legalFormMap.bvba = legalFormMap['bv']; // 'bvba' is abolished since 1 mei 2019 and is now just bv

  // Create a regex pattern from all legal form codes, sorted by length (longest first)
  const legalFormCodes = Object.keys(legalFormMap)
    .sort((a, b) => b.length - a.length) // Longest first to avoid partial matches
    .map(code => code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')); // Escape regex special chars
  
  // For front pattern: legal form must be at start, followed by space and company name
  const legalFormAtFrontPattern = new RegExp(`^(${legalFormCodes.join('|')})\\s+(.+)$`, 'i');
    // For end pattern: company name followed by space and legal form at end
  const legalFormAtEndPattern = new RegExp(`^(.+)\\s+(${legalFormCodes.join('|')})$`, 'i');
  const matchAtFront = fullName.match(legalFormAtFrontPattern);
  const matchAtEnd = fullName.match(legalFormAtEndPattern);
  
  if (matchAtFront) {
    const [, legalFormCode, officialName] = matchAtFront;
    const legalForm = legalFormMap[legalFormCode.toLowerCase()] || null;
    
    return { officialName: officialName.trim(), legalForm };
  }
  if (matchAtEnd) {
    const [, officialName, legalFormCode] = matchAtEnd;
    const legalForm = legalFormMap[legalFormCode.toLowerCase()] || null;
    
    return { officialName: officialName.trim(), legalForm };
  }
  if (isLikelyEenmanszaak(fullName)) {
    // If the name looks like a person (e.g. "Baerts, Peter"), we assume it's a sole proprietorship (eenmanszaak)
    return { officialName: fullName, legalForm: legalFormMap['zelfstandige'] };
  }
  
  // No legal form found
  return { officialName: fullName, legalForm: null };
};

const CITY_TRANSLATIONS = {
  'Bruxelles': 'Brussel',
  'Ixelles': 'Elsene',
  'Saint-Gilles': 'Sint-Gillis',
  'Saint-Josse-ten-Noode': 'Sint-Joost-ten-Node',
  'Schaerbeek': 'Schaarbeek',
  'Molenbeek-Saint-Jean': 'Sint-Jans-Molenbeek',
  'Orsmaal': 'Orsmaal-Gussenhoven',
  'Dilsen': 'Dilsen-Stokkem',
  'Glabbeek': 'Glabbeek-Zuurbemde',
  'Beveren': 'Beveren-Waas',
  'Comines': 'Komen-Waasten',
  'Heist': 'Heist-aan-Zee',
};

const getStreetAndHouseNumberFromStreetLine = (streetLine) => {
  let streetMatch;

  // Try format: "Street Name 123" or "Street Name 123a"
  streetMatch = streetLine.match(/^(.+?)\s+(\d+[a-zA-Z]*)$/);
  if (streetMatch) {
    return {
      street: toTitleCase(streetMatch[1].trim()),
      houseNumber: streetMatch[2].trim(),
    };
  }

  // Try format: "123 Street Name" or "123a Street Name"
  streetMatch = streetLine.match(/^(\d+[a-zA-Z]*)\s+(.+)$/);
  if (streetMatch) {
    return {
      street: toTitleCase(streetMatch[2].trim()),
      houseNumber: streetMatch[1].trim(),
    };
  }

  return { street: toTitleCase(streetLine.trim())}
};

const getSriAddress = (addressAsString, countryCode) => {
  if (!addressAsString) return null;
  
  // Determine if address uses comma or newline format
  let lines;
  if (addressAsString.trim().includes('\n')) {
    // Newline format: "Street 123\n1000 City"
    lines = addressAsString.trim().split('\n').map(line => line.trim());
  } else if (addressAsString.includes(',')) {
    // Comma format: "Street 123, City, LV-1082" or "Street 123, 1000 City"
    const parts = addressAsString.trim().split(',').map(part => part.trim()).filter(part => part);
    
    if (parts.length >= 3) {
      // Format: "Street 123, City, LV-1082"
      const street = parts[0]; // "Street 123"
      const city = parts[1]; // "City"
      const zipCode = parts[2]; // "LV-1082"
      lines = [street, `${zipCode} ${city}`]; // Convert to standard format
    } else if (parts.length === 2) {
      console.warn(`Is format Street 123, 1000 City also used???? ${addressAsString}`)
      // Format: "Street 123, 1000 City" 
      lines = parts;
    } else {
      return null;
    }
  }
  
  if (lines.length < 2) return null;

  // Handle 3-line format by ignoring the first line
  if (lines.length === 3) {
    console.warn(`3-line address format detected. Ignoring first line: "${lines[0]}" for address: ${addressAsString}`);
    lines = lines.slice(1); // Remove the first line, keep last 2 lines
  }
  
  // First line: street and house number
  // Split on first space followed by digit (street names don't contain "space + digit")
  const streetLine = lines[0];
  const { street, houseNumber } = getStreetAndHouseNumberFromStreetLine(streetLine);
  
  // Second line: zip code and city
  const cityLine = lines[1];
  const cityMatch = cityLine.match(/^(\S+)\s+(.+)$/); // Any non-space characters for zip code, rest is city
  
  if (!street || !cityMatch) return null;

  let zipCode = cityMatch[1].trim();
  const city = cityMatch[2].trim();
  if (countryCode === 'NL' && /^\d{4}[A-Z]{2}$/i.test(zipCode)) {
    zipCode = `${zipCode.slice(0, 4)} ${zipCode.slice(4).toUpperCase()}`;
  } else if (zipCode.startsWith(`${countryCode}-`)) {
    // for example LV-1082 in Latvian zipCodes
    zipCode = zipCode.substring(3); // Remove "countryCode-" prefix
  }
  
  return {
    street,
    houseNumber,
    zipCode,
    city: toTitleCase(CITY_TRANSLATIONS[city] || city), // Translate city if needed
    country: countryCode.toLowerCase(),
    countryHref: `/sam/commons/countries/${countryCode.toLowerCase()}`,
  };
}

const getSubCityByProperty = (subCities, openStreetMapResp, property) => {
  if (!openStreetMapResp || openStreetMapResp.length === 0 || !openStreetMapResp[0].address?.[property]) return null;

  const address = openStreetMapResp[0].address;
  if (!address) return null;

 const subCityMatchingProperty = subCities.find(
      (subCity) =>
        subCity.name === openStreetMapResp[0].address[property] ||
        subCity.name ===
          CITY_TRANSLATIONS[openStreetMapResp[0].address[property]]
    );
  if (!subCityMatchingProperty) return null;
  return subCityMatchingProperty;
};

const getBelgianSubCity = async (formModel, subCities) => {
  if (subCities && subCities.length === 1) {
    return subCities[0];
  }
  const qAddress = `${formModel.mail.street} ${formModel.mail.houseNumber}, ${formModel.mail.zipCode} ${formModel.mail.city}`;
  const openStreetMapResp = await getOpenStreetMapAddress(qAddress);
  const osmProperties = ['city_district', 'town', 'village', 'suburb'];
  for (const property of osmProperties) {
    const subCity = getSubCityByProperty(subCities, openStreetMapResp, property);
    if (subCity) {
      console.log(
        `Subcity ${subCity.name} found with OSM ${property} property for ${qAddress} (${formModel.fullName})`
      );
      return subCity;
    }
  }
  console.warn(`no subcity found for ${qAddress} (${formModel.fullName})`);

  const subCityMatchingCityName = subCities.find((subCity) => subCity.name === formModel.mail.city || subCity.name === CITY_TRANSLATIONS[formModel.mail.city]);
  if (subCityMatchingCityName) {
    return subCityMatchingCityName;
  }
  return null;
}

export const importViesOrg = async (vatNumber, formModel, dirtyResources) => {
  const countryCode = vatNumber.substring(0, 2).toUpperCase();
  const viesOrg = await getViesOrg(countryCode, vatNumber);
  if (!viesOrg.isValid) {
    throw { code: 'not.vat.registrered', message: `Deze onderneming is niet BTW-plichtig en de gegevens kunnen bij gevlog niet opgehaald worden uit vies.` };
  }
  const legalFormList = await fetchLegalForms();
  const { officialName, legalForm } = splitLegalFormAndName(viesOrg.name, legalFormList);
  formModel.description = `Geïmporteerd uit VIES (${viesOrg.requestDate}):\n${viesOrg.name}\n${viesOrg.address.trim()}`;
  dirtyResources.description = true;
  formModel.name = toTitleCase(officialName);
  dirtyResources.name = true;
  formModel.legalForm = legalForm;
  dirtyResources.legalForm = true;

  formModel.vatNumber = vatNumber;
  dirtyResources.vatNumber = true;
  formModel.companyNumber = extractCompanyNumberFromVat(vatNumber);
  dirtyResources.companyNumber = true;

  formModel.mail = getSriAddress(viesOrg.address, countryCode);
  dirtyResources.mail = true;
  if (formModel.mail.country === 'be') {
    const subCities = await getSubCityByZipCode(formModel.mail.zipCode);
    const subCity = await getBelgianSubCity(formModel, subCities.map((s) => ({ ...s, name: s.name.split(' (')[0] })));
    if (subCity) {
      formModel.mail.subCity = subCity.name;
      formModel.mail.subCityHref = subCity.$$meta.permalink;
      formModel.mail.cityHref = subCity.city.href;
    }
  }
};